import {
  Avatar,
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { MaterialIcon } from '../../ui/MaterialIcon';
import type { EntityPermission } from '../../../hooks/data/useEntityAccess';
import { getAvatarColor } from '../../../utils/avatarColor';

interface PersonRowProps {
  userId: string;
  name: string;
  avatar: string | null;
  isOwner: boolean;
  /**
   * True when this row represents the entity creator (and they are
   * NOT the workspace owner — `isOwner` takes precedence visually).
   * Drives the "Creator" sub-label and locks the permission picker
   * to MANAGE.  Notion convention.
   */
  isCreator: boolean;
  isCurrentUser: boolean;
  permission: EntityPermission;
  onChangePermission: ((next: EntityPermission) => Promise<void>) | null;
  onRemove: (() => Promise<void>) | null;
  /**
   * False when this person is listed but cannot actually open the entity (a
   * restricted or private ancestor stops their grant). Omitted = can open.
   */
  canOpen?: boolean;
  /** The collection to name when `canOpen` is false, if one explains it. */
  blockedByName?: string;
}

export function permissionLabel(p: EntityPermission): string {
  if (p === 'VIEW') return 'Can view';
  if (p === 'EDIT') return 'Can edit';
  return 'Full access';
}

/**
 * One person on the entity's access list.  Renders the avatar +
 * display name, the workspace-owner / creator sub-label when
 * applicable, and the permission picker.  When the row's
 * `onChangePermission` is null the picker renders disabled — used
 * for the workspace-owner pinned row and the entity-creator row,
 * neither of which can be downgraded.
 */
export function PersonRow({
  userId,
  name,
  avatar,
  isOwner,
  isCreator,
  isCurrentUser,
  permission,
  onChangePermission,
  onRemove,
  canOpen = true,
  blockedByName,
}: PersonRowProps): React.ReactElement {
  return (
    <HStack py={2.5} spacing={3} align="center">
      <Avatar
        size="sm"
        name={name}
        src={avatar ?? undefined}
        bg={getAvatarColor(userId).bg}
        color={getAvatarColor(userId).color}
      />
      <VStack align="flex-start" spacing={0} flex={1} minW={0}>
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="medium" color="text.primary" noOfLines={1}>
            {name}
          </Text>
          {isCurrentUser && (
            <Text fontSize="xs" color="text.subtle">
              (you)
            </Text>
          )}
        </HStack>
        {isOwner && (
          <Text fontSize="xs" color="text.subtle">
            Workspace owner
          </Text>
        )}
        {!isOwner && isCreator && (
          <Text fontSize="xs" color="text.subtle">
            Creator
          </Text>
        )}
        {!canOpen && (
          <HStack spacing={1} color="text.warning">
            <MaterialIcon name="block" boxSize="12px" color="inherit" />
            <Text fontSize="xs">
              {blockedByName
                ? `Can't open this: needs access to ${blockedByName}`
                : "Can't open this: a restricted item above it blocks access"}
            </Text>
          </HStack>
        )}
      </VStack>

      {/* Permission picker — disabled for owner / creator rows */}
      <Menu>
        <MenuButton
          as={Button}
          variant="ghost"
          size="sm"
          rightIcon={
            onChangePermission ? <MaterialIcon name="arrow_drop_down" boxSize="18px" /> : undefined
          }
          isDisabled={!onChangePermission}
          fontWeight="medium"
          color="text.secondary"
        >
          {permissionLabel(permission)}
        </MenuButton>
        {onChangePermission && (
          <MenuList minW="160px">
            {(['VIEW', 'EDIT', 'MANAGE'] as const).map((p) => (
              <MenuItem
                key={p}
                onClick={() => {
                  if (p !== permission) void onChangePermission(p);
                }}
              >
                <HStack spacing={2}>
                  {p === permission && <MaterialIcon name="check" boxSize="16px" />}
                  <Text>{permissionLabel(p)}</Text>
                </HStack>
              </MenuItem>
            ))}
            {onRemove && (
              <>
                <Box borderTopWidth="1px" borderColor="border.subtle" my={1} />
                <MenuItem
                  onClick={() => void onRemove()}
                  icon={<MaterialIcon name="person_remove" boxSize="16px" color="text.error" />}
                >
                  <Text color="text.error">Remove</Text>
                </MenuItem>
              </>
            )}
          </MenuList>
        )}
      </Menu>
    </HStack>
  );
}
